import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Masonry from "react-smart-masonry";
import { Dialog, Menu, Transition } from "@headlessui/react";
import ScrollToTop from "react-scroll-to-top";
import { ToastContainer } from "react-toastify";
import {
  Bookmark,
  Check,
  ChevronDown,
  Download,
  Eye,
  EyeOff,
  FileText,
  FolderOpen,
  MoreHorizontal,
  MoreVertical,
  Plus,
  Search,
  Share2,
  Star,
  StickyNote,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import "./Tags.scss";
import "../Bookmarks/Bookmarks.scss";
import "../Files/Files.scss";
import "../../shades/Bucket.scss";
import { ReactComponent as MySVG } from "../../assets/icons/7601886_top_arrow-update.svg";
import CreateTags from "./CreateTags";
import DeleteSubtag from "./DeleteSubtag";
import MoveTags from "./MoveTags";
import NewSingleCard from "../../shades/cards/NewSingleCard";
import ShareCard from "../../shades/cards/shareCard";
import BlackDismiss from "../../assets/icons/dismissButton.svg";
import { toastify } from "../../components/Toast";

const tagColors = [
  "#3b82f6",
  "#a855f7",
  "#22c55e",
  "#ef4444",
  "#ec4899",
  "#f97316",
  "#14b8a6",
  "#06b6d4",
  "#6366f1",
  "#eab308",
];

const contentFilterOptions = [
  { value: "recent-activity", label: "Recent Activity", apiValue: "Recent_Activity" },
  { value: "newest-first", label: "Newest First", apiValue: "Created_Date" },
  { value: "oldest-first", label: "Oldest First", apiValue: "Created_Date" },
  { value: "alphabetical", label: "Alphabetical", apiValue: "Created_Date" },
];

const getResolvedTheme = () => {
  const savedTheme = localStorage.getItem("theme");
  if (savedTheme === "dark") return "dark";
  if (savedTheme === "system" || !savedTheme) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
};

const getTagCount = (tag) =>
  Number(
    tag?.count ??
      tag?.contentCount ??
      tag?.cardsCount ??
      tag?.totalCards ??
      tag?.totalContent ??
      0
  ) || 0;

const getSearchableText = (item) => {
  const tagText = Array.isArray(item?.tags)
    ? item.tags.map((tag) => tag?.name || tag).join(" ")
    : "";
  const mediaText = Array.isArray(item?.content?.imageUrl)
    ? item.content.imageUrl.map((media) => media?.url || "").join(" ")
    : "";

  return [
    item?.title,
    item?.name,
    item?.note,
    item?.content?.html,
    item?.content?.sourceUrl,
    tagText,
    mediaText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const getFormat = (card) => {
  const media = card?.content?.imageUrl || [];
  const mediaItems = Array.isArray(media) ? media : [media];
  if (mediaItems.length > 1) return "carousel";
  const firstMedia = mediaItems[0];
  if (
    firstMedia?.urlType?.toLowerCase() === "video" ||
    /\.(mp4|mov|webm)(?:[?#]|$)/i.test(firstMedia?.url || "")
  ) {
    return "video";
  }
  return "image";
};

const fileImageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"];
const fileVideoExtensions = ["mp4", "mov", "avi", "webm", "mkv"];

const getFileExtension = (name = "") => {
  const extension = name.split(".").pop();
  return extension && extension !== name ? extension.toLowerCase() : "file";
};

const getFilePresentation = (extension) => {
  const type = extension.toLowerCase();
  if (type === "pdf") return { tone: "pdf", label: "PDF" };
  if (["doc", "docx", "rtf"].includes(type)) return { tone: "document", label: type.toUpperCase() };
  if (["xls", "xlsx", "csv"].includes(type)) return { tone: "sheet", label: type.toUpperCase() };
  if (["ppt", "pptx", "key"].includes(type)) return { tone: "presentation", label: type.toUpperCase() };
  if (fileImageExtensions.includes(type)) return { tone: "image", label: type.toUpperCase() };
  if (fileVideoExtensions.includes(type)) return { tone: "video", label: type.toUpperCase() };
  if (["mp3", "wav", "aac", "m4a", "ogg"].includes(type)) return { tone: "audio", label: type.toUpperCase() };
  if (["zip", "rar", "7z", "tar", "gz"].includes(type)) return { tone: "archive", label: type.toUpperCase() };
  if (["js", "jsx", "ts", "tsx", "html", "css", "json", "py", "php"].includes(type)) return { tone: "code", label: type.toUpperCase() };
  return { tone: "generic", label: type.toUpperCase().slice(0, 5) || "FILE" };
};

const getFileUrl = (file) => {
  const media = file?.content?.imageUrl;
  const firstMedia = Array.isArray(media) ? media[0] : media;
  return firstMedia?.url || "";
};

const getFileSize = (file) => {
  const media = Array.isArray(file?.content?.imageUrl)
    ? file.content.imageUrl[0]
    : file?.content?.imageUrl;
  const sizes = [
    file?.sizeInBytes,
    file?.fileSize,
    file?.size,
    file?.content?.sizeInBytes,
    file?.content?.fileSize,
    media?.sizeInBytes,
    media?.fileSize,
    media?.size,
    media?.metaData?.sizeInBytes,
    media?.metaData?.fileSize,
    media?.metaData?.size,
  ];
  return sizes.find((size) => size !== undefined && size !== null && size !== "");
};

const formatFileSize = (value) => {
  if (typeof value === "string" && Number.isNaN(Number(value))) return value.trim() || "—";
  const bytes = typeof value === "string" ? Number(value) : value;
  if (typeof bytes !== "number" || Number.isNaN(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toLocaleString(undefined, { maximumFractionDigits: 1 })} ${units[index]}`;
};

const formatFileDate = (date) => {
  if (!date || Number.isNaN(new Date(date).getTime())) return "—";
  return new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const getFileTagLabel = (file) => {
  const firstTag = Array.isArray(file?.tags) ? file.tags[0] : null;
  return typeof firstTag === "string" ? firstTag : firstTag?.name || "No tags";
};

const getFileNotePreview = (file) => String(file?.note || "")
  .replace(/<[^>]+>/g, " ")
  .replace(/\s+/g, " ")
  .trim();

function TaggedFileTypeIcon({ fileName }) {
  const { tone, label } = getFilePresentation(getFileExtension(fileName));

  return (
    <span className={`files-type-icon files-type-icon--${tone} files-type-icon--large`} aria-label={`${label} file`}>
      <svg viewBox="0 0 40 52" fill="none" aria-hidden="true">
        <path className="files-type-icon__paper" d="M4 4C4 1.79086 5.79086 0 8 0H26L36 10V48C36 50.2091 34.2091 52 32 52H8C5.79086 52 4 50.2091 4 48V4Z" stroke="currentColor" strokeWidth="2" />
        <path d="M26 0V6C26 8.20914 27.7909 10 30 10H36" stroke="currentColor" strokeWidth="2" />
        <line x1="10" y1="22" x2="30" y2="22" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
        <line x1="10" y1="28" x2="26" y2="28" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
        <line x1="10" y1="34" x2="28" y2="34" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" />
      </svg>
      <span>{label}</span>
    </span>
  );
}

function TaggedFilePreview({ file }) {
  const extension = getFileExtension(file?.title);
  const fileUrl = getFileUrl(file);

  if (fileUrl && fileImageExtensions.includes(extension)) {
    return <img className="files-media-preview files-media-preview--large" src={fileUrl} alt={file?.title || "File preview"} />;
  }

  if (fileUrl && fileVideoExtensions.includes(extension)) {
    return (
      <span className="files-media-preview files-media-preview--large files-media-preview--video">
        <video src={fileUrl} muted playsInline preload="metadata" aria-label={`${file?.title || "File"} video preview`} />
        <span className="files-media-preview__play" aria-hidden="true" />
      </span>
    );
  }

  return <TaggedFileTypeIcon fileName={file?.title} />;
}

function flattenTags(tags, depth = 0) {
  return (tags || []).flatMap((tag) => [
    { ...tag, depth },
    ...flattenTags(tag?.subTags, depth + 1),
  ]);
}

function TagList() {
  const navigate = useNavigate();
  const [subtags, setSubtags] = useState([]);
  const [searchedTags, setSearchedTags] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [cardList, setCardList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [bookmarkList, setBookmarkList] = useState([]);
  const [cardListCount, setCardListCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isContentLoading, setIsContentLoading] = useState(false);

  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedType, setSelectedType] = useState("Any");
  const [isUnassigned, setIsUnassigned] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [contentSearchQuery, setContentSearchQuery] = useState("");
  const [viewFilter, setViewFilter] = useState("all");
  const [selectedFormats, setSelectedFormats] = useState([]);
  const [sortBy, setSortBy] = useState("recent-activity");
  const [tagSortBy, setTagSortBy] = useState("count");
  const [showCopy, setShowCopy] = useState(true);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showSearchSummary, setShowSearchSummary] = useState(false);

  const [popup, setPopup] = useState(false);
  const [deleteTagPopup, setDeleteTagPopup] = useState(false);
  const [movePopup, setMovePopup] = useState(false);
  const [deleteContentPopup, setDeleteContentPopup] = useState(false);
  const [bucketOperation, setBucketOperation] = useState("Add");
  const [bucketNameText, setBucketNameText] = useState("");
  const [bucketUpdateId, setBucketUpdateId] = useState(0);
  const [parentId, setParentId] = useState(null);
  const [parentDelete, setParentDelete] = useState(0);
  const [dataVersion, setDataVersion] = useState(0);
  const [contentVersion, setContentVersion] = useState(0);

  const [bucketSharePopup, setBucketSharePopup] = useState(false);
  const [shareBucketId, setShareBucketId] = useState("");
  const [appTheme, setAppTheme] = useState(getResolvedTheme);
  const contentAreaRef = useRef(null);
  const [masonryColumns, setMasonryColumns] = useState(3);

  const isDark = appTheme === "dark";
  const tagSource = searchedTags || subtags;
  const allTags = useMemo(() => flattenTags(subtags), [subtags]);
  const displayTags = useMemo(() => {
    const tags = flattenTags(tagSource);
    return [...tags].sort((first, second) => {
      if (tagSortBy === "alphabetical") {
        return (first?.name || "").localeCompare(second?.name || "");
      }
      return getTagCount(second) - getTagCount(first);
    });
  }, [tagSource, tagSortBy]);

  const selectedTags = useMemo(
    () =>
      selectedTagIds
        .map((tagId) => allTags.find((tag) => tag._id === tagId))
        .filter(Boolean),
    [allTags, selectedTagIds]
  );

  const selectedSort = contentFilterOptions.find(
    (option) => option.value === sortBy
  );

  useEffect(() => {
    const handleThemeChange = () => setAppTheme(getResolvedTheme());
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    window.addEventListener("storage", handleThemeChange);
    mediaQuery.addEventListener("change", handleThemeChange);
    return () => {
      window.removeEventListener("storage", handleThemeChange);
      mediaQuery.removeEventListener("change", handleThemeChange);
    };
  }, []);

  // The app has two sidebars, so viewport breakpoints alone can overestimate
  // the space available for cards (especially at 125% display scaling).
  // Measure the actual results area and change columns before a card can clip.
  useEffect(() => {
    if (isLoading || !contentAreaRef.current) return undefined;

    const resultsArea = contentAreaRef.current;
    const updateColumns = () => {
      const width = resultsArea.getBoundingClientRect().width;
      setMasonryColumns(width >= 1000 ? 3 : width >= 640 ? 2 : 1);
    };

    updateColumns();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateColumns);
      return () => window.removeEventListener("resize", updateColumns);
    }

    const observer = new ResizeObserver(updateColumns);
    observer.observe(resultsArea);

    return () => observer.disconnect();
  }, [isLoading]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsLoading(false);
      return;
    }

    const loadPageData = async () => {
      setIsLoading(true);
      try {
        const [tagsResponse, bucketsResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}tags/list`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API}buckets/user-buckets`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setSubtags(tagsResponse?.data?.data || []);
        setBuckets(bucketsResponse?.data?.data?.bucketList || []);
      } catch (error) {
        console.error("Unable to load tag data", error);
        toastify("error", error?.response?.data?.message || "Unable to load tags");
      } finally {
        setIsLoading(false);
      }
    };

    loadPageData();
  }, [dataVersion]);

  useEffect(() => {
    const query = tagSearchQuery.trim();
    if (!query) {
      setSearchedTags(null);
      return undefined;
    }

    const token = localStorage.getItem("token");
    const searchTimeout = window.setTimeout(async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}tags/list/${encodeURIComponent(query)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSearchedTags(response?.data?.data || []);
      } catch (error) {
        console.error("Unable to search tags", error);
        setSearchedTags([]);
      }
    }, 250);

    return () => window.clearTimeout(searchTimeout);
  }, [tagSearchQuery]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchContent = async () => {
      setIsContentLoading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API}tags/getCards-by-tagId`,
          {
            tagIds: selectedTagIds,
            filterValue: selectedSort?.apiValue || "Recent_Activity",
            selectedType,
            limit: 100,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = response?.data?.data || {};
        setCardList(data.cardsList || []);
        setFileList(data.filesList || []);
        setBookmarkList(data.bookmarksList || []);
        setCardListCount(
          Number(data.totalCards || 0) +
            Number(data.totalBookmarks || 0) +
            Number(data.totalFiles || 0)
        );
      } catch (error) {
        console.error("Unable to load tagged content", error);
        setCardList([]);
        setFileList([]);
        setBookmarkList([]);
        setCardListCount(0);
      } finally {
        setIsContentLoading(false);
      }
    };

    fetchContent();
  }, [selectedTagIds, selectedSort, selectedType, contentVersion]);

  const refreshTags = () => {
    setDataVersion((version) => version + 1);
    setSearchedTags(null);
  };

  const refreshContent = () => {
    setContentVersion((version) => version + 1);
  };

  const openCreateTag = () => {
    setParentId(null);
    setBucketOperation("Add");
    setBucketNameText("");
    setPopup(true);
  };

  const handleSelectionMode = (mode) => {
    setSelectedType(mode);
    setIsUnassigned(false);

    if (mode === "All") {
      setSelectedTagIds([...new Set(allTags.map((tag) => tag._id).filter(Boolean))]);
    } else {
      setSelectedTagIds([]);
    }
  };

  const toggleTag = (tagId) => {
    setIsUnassigned(false);
    setSelectedTagIds((selected) =>
      selected.includes(tagId)
        ? selected.filter((id) => id !== tagId)
        : [...selected, tagId]
    );
  };

  const toggleUnassigned = () => {
    setIsUnassigned((enabled) => !enabled);
    setSelectedTagIds([]);
    setContentVersion((version) => version + 1);
  };

  const clearSelectedTags = () => {
    setSelectedTagIds([]);
    setIsUnassigned(false);
  };

  const handleTagAction = async (tag, action) => {
    if (action === "rename") {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}tags/single-tag/${tag._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setBucketNameText(response?.data?.data?.name || tag.name);
      } catch (error) {
        setBucketNameText(tag.name);
      }
      setBucketUpdateId(tag._id);
      setBucketOperation("Update");
      setPopup(true);
      return;
    }

    if (action === "add-subtag") {
      setBucketUpdateId(tag._id);
      setParentId(tag._id);
      setBucketOperation("subbucket");
      setBucketNameText("");
      setPopup(true);
      return;
    }

    if (action === "move") {
      setBucketUpdateId(tag._id);
      setMovePopup(true);
      return;
    }

    if (action === "delete") {
      setBucketUpdateId(tag._id);
      setParentDelete(tag?.subTags?.length || 0);
      setDeleteTagPopup(true);
    }
  };

  const handleCheckboxChange = (event) => {
    const value = event.target.value;
    setSelectedItems((items) =>
      event.target.checked
        ? [...new Set([...items, value])]
        : items.filter((item) => item !== value)
    );
  };

  const deleteSelectedCards = async () => {
    if (!selectedItems.length) return;
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${process.env.REACT_APP_API}cards/delete`, {
        data: { deletedIds: selectedItems },
        headers: { Authorization: `Bearer ${token}` },
      });
      toastify("success", "Selected cards deleted successfully");
      setSelectedItems([]);
      setDeleteContentPopup(false);
      refreshContent();
    } catch (error) {
      toastify("error", error?.response?.data?.message || "Unable to delete cards");
      setDeleteContentPopup(false);
    }
  };

  const handleSharePop = (id) => {
    setShareBucketId(id);
    setBucketSharePopup(true);
  };

  const updateFileStar = async (file) => {
    const nextStarredState = !file?.starred;
    const token = localStorage.getItem("token");

    setFileList((files) => files.map((item) => (
      item?._id === file?._id ? { ...item, starred: nextStarredState } : item
    )));

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: file?._id, starred: nextStarredState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response?.data?.message || "File updated.");
    } catch (error) {
      setFileList((files) => files.map((item) => (
        item?._id === file?._id ? { ...item, starred: !nextStarredState } : item
      )));
      toastify("error", error?.response?.data?.message || "Unable to update file.");
    }
  };

  const downloadFile = async (file) => {
    const fileUrl = getFileUrl(file);
    if (!fileUrl) {
      toastify("error", "This file is not available for download.");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}cards/download-file`,
        { fileUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const base64Data = response?.data?.data?.fileBase64;
      if (!base64Data) throw new Error("No download data received");

      const bytes = Uint8Array.from(atob(base64Data), (character) => character.charCodeAt(0));
      const blob = new Blob([bytes], { type: getFileExtension(file?.title) });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = file?.title || `file.${getFileExtension(file?.title)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Unable to download file:", error);
      toastify("error", error?.response?.data?.message || "Unable to download file.");
    }
  };

  const toggleFormat = (format) => {
    setSelectedFormats((formats) =>
      formats.includes(format)
        ? formats.filter((item) => item !== format)
        : [...formats, format]
    );
  };

  const filterAndSortItems = (items) => {
    const query = contentSearchQuery.trim().toLowerCase();
    let results = items.filter((item) => {
      const matchesQuery = !query || getSearchableText(item).includes(query);
      const matchesFormat =
        !selectedFormats.length ||
        selectedFormats.includes(getFormat(item));
      return matchesQuery && matchesFormat;
    });

    return [...results].sort((first, second) => {
      if (sortBy === "alphabetical") {
        return (first?.title || first?.name || "").localeCompare(
          second?.title || second?.name || ""
        );
      }
      const firstDate = new Date(first?.updatedAt || first?.createdAt || 0).getTime();
      const secondDate = new Date(second?.updatedAt || second?.createdAt || 0).getTime();
      return sortBy === "oldest-first" ? firstDate - secondDate : secondDate - firstDate;
    });
  };

  const filteredCards = filterAndSortItems(cardList);
  const filteredFiles = filterAndSortItems(fileList);
  const filteredBookmarks = filterAndSortItems(bookmarkList);
  const totalResults =
    filteredCards.length + filteredFiles.length + filteredBookmarks.length;
  const canShowResults = selectedTagIds.length > 0 || isUnassigned;

  const renderTagMenu = (tag) => (
    <Menu as="div" className="tags-v2__tag-menu" onClick={(event) => event.stopPropagation()}>
      <Menu.Button
        aria-label={`Actions for ${tag.name}`}
        className="tags-v2__icon-button tags-v2__tag-menu-button"
      >
        <MoreVertical size={16} />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="tags-v2__menu">
          <Menu.Item>
            {({ active }) => (
              <button
                className={active ? "is-active" : ""}
                onClick={() => handleTagAction(tag, "rename")}
              >
                Rename
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={active ? "is-active" : ""}
                onClick={() => handleTagAction(tag, "add-subtag")}
              >
                Add subtag
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={active ? "is-active" : ""}
                onClick={() => handleTagAction(tag, "move")}
              >
                Move tag
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                className={`tags-v2__menu-danger ${active ? "is-active" : ""}`}
                onClick={() => handleTagAction(tag, "delete")}
              >
                Delete
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );

  return (
    <>
      {isLoading ? (
        <div className="App-loading">
          <div className="App-logo" title="Zimzee" />
        </div>
      ) : (
        <div className={`tags-v2 ${isDark ? "tags-v2--dark" : ""}`}>
          {bucketSharePopup && (
            <div
              className="filesuploadpopup relative !z-[999]"
              style={{ height: "100%" }}
              onClick={() => setBucketSharePopup(false)}
            >
              <div
                className="subinviteupload-section"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="teaminvitesection flex">
                  <div className="teammembers-content">Share</div>
                  <button
                    className="dismissSubfiles-content"
                    onClick={() => setBucketSharePopup(false)}
                    aria-label="Close share dialog"
                  >
                    <img src={BlackDismiss} alt="" />
                  </button>
                </div>
                <ShareCard
                  shareBucketId={shareBucketId}
                  closeSharePop={() => setBucketSharePopup(false)}
                  cardsRefresh={refreshContent}
                />
              </div>
            </div>
          )}

          <aside className="tags-v2__sidebar">
            <div className="tags-v2__sidebar-inner">
              <div className="tags-v2__sidebar-heading">
                <h1>Tags</h1>
                <button className="tags-v2__add-button" onClick={openCreateTag}>
                  <Plus size={16} />
                  Add
                </button>
              </div>

              <div className="tags-v2__tag-total">
                <Tag size={16} />
                <span>{allTags.length} Tags in Total</span>
              </div>

              <label className="tags-v2__search">
                <Search size={16} />
                <input
                  value={tagSearchQuery}
                  onChange={(event) => setTagSearchQuery(event.target.value)}
                  placeholder="Search tags..."
                  type="search"
                />
              </label>

              <div className="tags-v2__sort-tags">
                <span>Sort by:</span>
                <div>
                  <button
                    className={tagSortBy === "count" ? "is-selected" : ""}
                    onClick={() => setTagSortBy("count")}
                  >
                    Count
                  </button>
                  <button
                    className={tagSortBy === "alphabetical" ? "is-selected" : ""}
                    onClick={() => setTagSortBy("alphabetical")}
                  >
                    A-Z
                  </button>
                </div>
              </div>

              <div className="tags-v2__filter-mode" role="radiogroup" aria-label="Tag filter mode">
                {["All", "Any"].map((mode) => (
                  <button
                    key={mode}
                    role="radio"
                    aria-checked={selectedType === mode}
                    className={selectedType === mode ? "is-selected" : ""}
                    onClick={() => handleSelectionMode(mode)}
                  >
                    <span className="tags-v2__radio-dot" />
                    {mode} Selected
                  </button>
                ))}
              </div>

              <button
                className={`tags-v2__unassigned ${isUnassigned ? "is-selected" : ""}`}
                onClick={toggleUnassigned}
              >
                <FolderOpen size={16} />
                <span>Unassigned content</span>
              </button>

              <div className="tags-v2__tag-list">
                {displayTags.map((tag, index) => {
                  const isSelected = selectedTagIds.includes(tag._id);
                  return (
                    <div
                      key={tag._id}
                      className={`tags-v2__tag-row ${isSelected ? "is-selected" : ""}`}
                      style={{ "--tag-depth": tag.depth || 0 }}
                    >
                      <button
                        className="tags-v2__tag-select"
                        onClick={() => toggleTag(tag._id)}
                      >
                        <span
                          className="tags-v2__tag-color"
                          style={{ backgroundColor: tagColors[index % tagColors.length] }}
                        />
                        <span className="tags-v2__tag-name">{tag.name}</span>
                        {getTagCount(tag) > 0 && (
                          <span className="tags-v2__tag-count">{getTagCount(tag)}</span>
                        )}
                      </button>
                      {renderTagMenu(tag)}
                    </div>
                  );
                })}
                {!displayTags.length && (
                  <p className="tags-v2__no-tags">No tags found.</p>
                )}
              </div>
            </div>
          </aside>

          <main className="tags-v2__content">
            <div className="tags-v2__content-inner" ref={contentAreaRef}>
              <div className="tags-v2__toolbar">
                <div className="tags-v2__selected-tags">
                  <div className="tags-v2__selection-summary">
                    <span>{isUnassigned ? "Unassigned content" : `${selectedTagIds.length} Tag(s) Selected`}</span>
                    {canShowResults && <strong>{cardListCount}</strong>}
                  </div>
                  {selectedTags.map((tag) => (
                    <span className="tags-v2__chip" key={tag._id}>
                      {tag.name}
                      <button onClick={() => toggleTag(tag._id)} aria-label={`Remove ${tag.name}`}>
                        <X size={13} />
                      </button>
                    </span>
                  ))}
                  {selectedTagIds.length > 0 && (
                    <button className="tags-v2__clear" onClick={clearSelectedTags}>
                      Clear all
                    </button>
                  )}
                </div>

                <div className="tags-v2__view-toggle" aria-label="Content type filter">
                  {[
                    ["all", "All"],
                    ["cards", "Cards"],
                    ["files", "Files"],
                    ["bookmarks", "Bookmarks"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      className={viewFilter === value ? "is-selected" : ""}
                      onClick={() => setViewFilter(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="tags-v2__controls">
                <div className="tags-v2__result-search-wrap">
                  <label className="tags-v2__search tags-v2__result-search">
                    <Search size={18} />
                    <input
                      value={contentSearchQuery}
                      onChange={(event) => {
                        setContentSearchQuery(event.target.value);
                        setShowSearchSummary(Boolean(event.target.value));
                      }}
                      onFocus={() => setShowSearchSummary(Boolean(contentSearchQuery))}
                      placeholder="Search in results..."
                      type="search"
                    />
                  </label>
                  {showSearchSummary && (
                    <div className="tags-v2__search-summary">
                      <div>
                        <strong>Search Results</strong>
                        <span>{totalResults} results</span>
                      </div>
                      <p>
                        {filteredCards.length} in cards · {filteredFiles.length} in files · {filteredBookmarks.length} in bookmarks
                      </p>
                    </div>
                  )}
                </div>

                <div className="tags-v2__dropdown-wrap">
                  <button className="tags-v2__control-button" onClick={() => setShowFormatMenu((open) => !open)}>
                    Format ({selectedFormats.length}) <ChevronDown size={16} />
                  </button>
                  {showFormatMenu && (
                    <div className="tags-v2__control-menu">
                      {["video", "image", "carousel"].map((format) => (
                        <label key={format}>
                          <input
                            type="checkbox"
                            checked={selectedFormats.includes(format)}
                            onChange={() => toggleFormat(format)}
                          />
                          {format[0].toUpperCase() + format.slice(1)}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="tags-v2__dropdown-wrap">
                  <button className="tags-v2__control-button" onClick={() => setShowSortMenu((open) => !open)}>
                    {selectedSort?.label} <ChevronDown size={16} />
                  </button>
                  {showSortMenu && (
                    <div className="tags-v2__control-menu">
                      {contentFilterOptions.map((option) => (
                        <button
                          key={option.value}
                          className={sortBy === option.value ? "is-selected" : ""}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortMenu(false);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  className={`tags-v2__control-button ${showCopy ? "is-selected" : ""}`}
                  onClick={() => setShowCopy((visible) => !visible)}
                >
                  {showCopy ? <Eye size={16} /> : <EyeOff size={16} />}
                  Show Copy
                </button>

                {selectedItems.length > 0 && (
                  <button
                    className="tags-v2__delete-selected"
                    onClick={() => setDeleteContentPopup(true)}
                  >
                    <Trash2 size={16} /> Delete ({selectedItems.length})
                  </button>
                )}
              </div>

              {!canShowResults ? (
                <EmptyState
                  icon={<Tag size={48} />}
                  title="Select tags to view content"
                  text="Choose one or more tags from the sidebar to filter your content."
                />
              ) : isContentLoading ? (
                <div className="tags-v2__results-loading">
                  <div className="App-loading">
                    <div className="App-logo" title="Zimzee" />
                  </div>
                </div>
              ) : totalResults === 0 ? (
                <EmptyState
                  icon={<Search size={48} />}
                  title="No results found"
                  text="Try selecting different tags, changing the filter mode, or clearing a search."
                />
              ) : (
                <div className="tags-v2__results">
                  {(viewFilter === "all" || viewFilter === "cards") && filteredCards.length > 0 && (
                    <ContentSection title="Cards" count={filteredCards.length} icon={<Tag size={18} />}>
                      <Masonry
                        className="tags-v2__masonry"
                        columns={masonryColumns}
                        gap={masonryColumns === 1 ? 14 : 18}
                      >
                        {filteredCards.map((card) => (
                          <NewSingleCard
                            key={card._id}
                            buckets={buckets}
                            handleSharePop={handleSharePop}
                            starred={card.starred}
                            cardUpdate={refreshContent}
                            cardId={card._id}
                            cardType={card.cardType}
                            sharedLength={card.shared?.length || 0}
                            title={card.title}
                            content={card.content}
                            note={card.note}
                            tags={card.tags}
                            postDetail={card.postDetail || ""}
                            updated={card.updatedAt}
                            handleCheckboxChange={handleCheckboxChange}
                            selectedItems={selectedItems}
                            aiData={card?.content?.extractedData || card?.extractedData}
                            adData={card?.adData}
                            showCopy={showCopy}
                          />
                        ))}
                      </Masonry>
                    </ContentSection>
                  )}

                  {(viewFilter === "all" || viewFilter === "files") && filteredFiles.length > 0 && (
                    <ContentSection title="Files" count={filteredFiles.length} icon={<FileText size={18} />}>
                      <div className="tags-v2__file-grid">
                        {filteredFiles.map((file) => (
                          <TaggedFileCard
                            key={file._id}
                            file={file}
                            selected={selectedItems.includes(file?._id)}
                            onSelect={handleCheckboxChange}
                            onStar={() => updateFileStar(file)}
                            onShare={() => handleSharePop(file?._id)}
                            onDownload={() => downloadFile(file)}
                            onOpen={() => navigate(`/app/card-detail/${file?._id}?card=file`)}
                          />
                        ))}
                      </div>
                    </ContentSection>
                  )}

                  {(viewFilter === "all" || viewFilter === "bookmarks") && filteredBookmarks.length > 0 && (
                    <ContentSection title="Bookmarks" count={filteredBookmarks.length} icon={<Bookmark size={18} />}>
                      <Masonry
                        className="tags-v2__masonry"
                        columns={masonryColumns}
                        gap={masonryColumns === 1 ? 14 : 18}
                      >
                        {filteredBookmarks.map((bookmark) => (
                          <NewSingleCard
                            key={bookmark._id}
                            buckets={buckets}
                            handleSharePop={handleSharePop}
                            starred={bookmark.starred}
                            cardId={bookmark._id}
                            cardType={bookmark.cardType || "bookmark"}
                            sharedLength={bookmark.shared?.length || 0}
                            title={bookmark.title}
                            content={bookmark.content}
                            note={bookmark.note}
                            tags={bookmark.tags}
                            postDetail={bookmark.postDetail || {}}
                            updated={bookmark.createdAt}
                            handleCheckboxChange={handleCheckboxChange}
                            selectedItems={selectedItems}
                            cardUpdate={refreshContent}
                            aiData={bookmark?.content?.extractedData || bookmark?.extractedData}
                            adData={bookmark?.adData}
                            showCopy={showCopy}
                            detailUrl={`/app/bookmark-detail/${bookmark._id}`}
                          />
                        ))}
                      </Masonry>
                    </ContentSection>
                  )}
                </div>
              )}
            </div>
          </main>

          {popup && (
            <CreateTags
              bucketUpdate={refreshTags}
              changeTxt={(event) => setBucketNameText(event.target.value)}
              bucketOperation={bucketOperation}
              bucketNameText={bucketNameText}
              bucketUpdateId={bucketUpdateId}
              parent={parentId}
              setPopup={setPopup}
            />
          )}
          {movePopup && (
            <MoveTags
              bucketUpdate={refreshTags}
              bucketUpdateId={bucketUpdateId}
              parent={parentId}
              setPopup={setMovePopup}
              subtags={subtags}
            />
          )}
          {deleteTagPopup && (
            <DeleteSubtag
              bucketUpdate={refreshTags}
              bucketUpdateId={bucketUpdateId}
              parent={parentId}
              setPopup={setDeleteTagPopup}
              parentDelete={parentDelete}
            />
          )}

          <Transition appear show={deleteContentPopup} as={Fragment}>
            <Dialog as="div" className="relative z-[1000]" onClose={() => setDeleteContentPopup(false)}>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-black/40" />
              </Transition.Child>
              <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className={`tags-v2__delete-dialog ${isDark ? "tags-v2__delete-dialog--dark" : ""}`}>
                      <Dialog.Title>Delete selected cards?</Dialog.Title>
                      <p>This will permanently delete {selectedItems.length} selected card{selectedItems.length === 1 ? "" : "s"}.</p>
                      <div>
                        <button onClick={() => setDeleteContentPopup(false)}>Cancel</button>
                        <button onClick={deleteSelectedCards}>Delete</button>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition>

          <ToastContainer />
        </div>
      )}
      <ScrollToTop smooth color="#fff" component={<MySVG />} />
    </>
  );
}

function TaggedFileCard({ file, selected, onSelect, onStar, onShare, onDownload, onOpen }) {
  const tagLabel = getFileTagLabel(file);
  const notePreview = getFileNotePreview(file);

  return (
    <article
      className={`files-grid-card tags-v2__file-card ${selected ? "is-selected" : ""}`}
      onClick={onOpen}
    >
      <div className="files-grid-card__tools">
        <label className={`files-checkbox ${selected ? "is-selected" : ""}`} onClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            value={file?._id}
            checked={selected}
            onChange={onSelect}
            aria-label={`Select ${file?.title || "file"}`}
          />
          <span>{selected && <Check size={12} strokeWidth={3} />}</span>
        </label>
        <button
          type="button"
          className={`files-star ${file?.starred ? "files-star--active" : ""}`}
          onClick={(event) => {
            event.stopPropagation();
            onStar();
          }}
          aria-label={file?.starred ? "Remove star" : "Star file"}
        >
          <Star size={17} fill={file?.starred ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="files-grid-card__preview">
        <TaggedFilePreview file={file} />
      </div>

      <div className="files-grid-card__body">
        <strong title={file?.title}>{file?.title || "Untitled file"}</strong>
        <span>{formatFileSize(getFileSize(file))} · {formatFileDate(file?.createdAt || file?.updatedAt)}</span>
        <div>
          <span className={`files-tag ${tagLabel === "No tags" ? "files-tag--empty" : ""}`}>{tagLabel}</span>
          {notePreview && <span className="files-note-indicator" title={notePreview}><StickyNote size={16} /></span>}
          <Menu as="div" className="files-action-menu" onClick={(event) => event.stopPropagation()}>
            <Menu.Button type="button" className="files-icon-button files-icon-button--quiet" aria-label={`Actions for ${file?.title || "file"}`}>
              <MoreHorizontal size={18} aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="files-action-menu__items">
                <Menu.Item>
                  {({ active }) => <button type="button" className={active ? "is-active" : ""} onClick={onOpen}><Eye size={16} /> Open details</button>}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => <button type="button" className={active ? "is-active" : ""} onClick={onDownload}><Download size={16} /> Download</button>}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => <button type="button" className={active ? "is-active" : ""} onClick={onShare}><Share2 size={16} /> Share</button>}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </article>
  );
}

function ContentSection({ title, count, icon, children }) {
  return (
    <section className="tags-v2__content-section">
      <header>
        <div>
          {icon}
          <h2>{title}</h2>
        </div>
        <span>{count} {count === 1 ? "result" : "results"}</span>
      </header>
      {children}
    </section>
  );
}

function EmptyState({ icon, title, text }) {
  return (
    <div className="tags-v2__empty-state">
      <div>{icon}</div>
      <h2>{title}</h2>
      <p>{text}</p>
    </div>
  );
}

export default TagList;
